import { Controller } from '@hotwired/stimulus'
import * as Turbo from '@hotwired/turbo'
import { Modal } from 'bootstrap'

export default class extends Controller {
  static values = {
    src: String,
    method: { type: String, default: 'get' },
    payload: Object,
  }

  disconnect() {
    this.unregisterCacheCleanup()
    if (this.bsmodal) {
      this.bsmodal.hide()
    }
  }

  // Turbo caches the current DOM before navigating away (e.g. clicking a link inside the modal).
  // With a modal open, the snapshot captures its backdrop and the body's modal-open class, which
  // then reappear orphaned on back/forward. Strip those artifacts from the cached snapshot.
  registerCacheCleanup() {
    if (this.beforeCache) return

    this.beforeCache = () => this.cleanupModalArtifacts()
    document.addEventListener('turbo:before-cache', this.beforeCache)
  }

  unregisterCacheCleanup() {
    if (!this.beforeCache) return

    document.removeEventListener('turbo:before-cache', this.beforeCache)
    this.beforeCache = null
  }

  cleanupModalArtifacts() {
    this.frame?.remove()
    this.modalElement?.remove()
    document.querySelectorAll('.modal-backdrop').forEach((el) => {
      el.remove()
    })
    document.body.classList.remove('modal-open')
    document.body.style.removeProperty('overflow')
    document.body.style.removeProperty('padding-right')
  }

  load() {
    if (this.isGetRequest()) {
      this.loadWithTurboFrame()
    } else {
      this.submitForm()
    }
  }

  loadWithTurboFrame() {
    this.frame = document.createElement('turbo-frame')
    this.frame.id = 'modal'
    this.frame.src = this.srcValue

    this.frame.addEventListener(
      'turbo:frame-load',
      () => {
        this.open()
      },
      { once: true },
    )

    this.frame.addEventListener('turbo:frame-missing', (event) => {
      this.redirectIfNeeded(event)
    })

    document.body.appendChild(this.frame)
  }

  open() {
    const root = this.frame.firstElementChild

    this.bsmodal = new Modal(root)
    this.bsmodal.show()
    this.registerCacheCleanup()

    root.addEventListener('hidden.bs.modal', () => {
      this.close()
    })
  }

  close() {
    this.unregisterCacheCleanup()
    this.frame.remove()
  }

  submitForm() {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    const options = {
      method: this.methodValue.toUpperCase(),
      headers: {
        'X-CSRF-Token': csrfToken || '',
      },
    }

    if (this.hasPayloadValue) {
      options.body = JSON.stringify(this.payloadValue)
      options.headers['Content-Type'] = 'application/json'
    }

    Turbo.fetch(this.srcValue, options)
      .then((response) => response.text())
      .then((html) => {
        // Create container for modal
        const container = document.createElement('div')
        container.innerHTML = html

        // Extract the modal element
        const modal = container.querySelector('.modal') || container.firstElementChild

        if (modal) {
          document.body.appendChild(modal)
          this.modalElement = modal
          this.bsmodal = new Modal(modal)
          this.bsmodal.show()
          this.registerCacheCleanup()

          modal.addEventListener(
            'hidden.bs.modal',
            () => {
              this.unregisterCacheCleanup()
              modal.remove()
            },
            { once: true },
          )
        }
      })
      .catch((error) => console.error('Modal load error:', error))
  }

  isGetRequest() {
    return this.methodValue.toLowerCase() === 'get'
  }

  redirectIfNeeded(event) {
    if (event.detail.response.redirected) {
      event.preventDefault()
      event.detail.visit(event.detail.response)
    }
  }
}
